/**
 * OpenOrders Component
 * Dashboard cards showing open orders per employee with status breakdowns and toggleable order lists.
 */

import React, { useState, useEffect } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

export interface OrderStatusEntry {
    status: string
    count: number
    href: string
    hasSeparatorBefore?: boolean
    datefield?: string
    end_time?: string
}

export interface OrderStatusGroupedEntry {
    category: string; statuses: OrderStatusEntry[]
}

export interface OpenOrderUser {
    userId: string
    name: string
    photoUrl?: string | null
    color: string
    font: string
    adminLevel: number
    openCount: number
    isAuditingCount: number
    statuses: OrderStatusEntry[]
    assignedToday: number
    invoicedToday: number

    /**
     * placeholder for returning data in categorized format so we don't have to hardcode statuses in this file
     */
    statusesGrouped?: OrderStatusGroupedEntry[]
}

export interface OpenOrdersProps {
    users: OpenOrderUser[]
    animate?: boolean
}

/** Inline color so light-theme `a { color: #fff !important }` rules do not win on the link pill. */
const AUDITING_PILL_LABEL_STYLE: React.CSSProperties = { color: '#3b82f6' }
const AVATAR_PHOTO_STYLE: React.CSSProperties = {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
}

const STATUS_SECTIONS: { label: string; statuses: string[] }[] = [
    {
        label: 'OPEN',
        statuses: [
            'ANALYZE',
            'ASSIGNED',
            'AUDIT',
            'FOLLOW UP',
            'IN PROGRESS',
            'IN PROGRESS-EO',
            'INTERNAL TASK',
            'NEW',
            'WAITING',
            'WAITING REVIEW',
        ]
    },
    {
        label: 'UPDATES',
        statuses: [
            'UPDATE',
            'UPDATE-IN PROG',
            'UPDATE-WAIT',
        ]
    },
    {
        label: 'PROJECTS',
        statuses: [
            'P-WAIT BLDG DEPT',
            'P-WAIT CLIENT',
            'P-WAIT STATUS CHG',
            'P-WAIT VENDOR',
        ]
    },
    {
        label: 'OTHER',
        statuses: [
            'MGR ESCALATION',
            'REFUND-WAIT',
            'WAIT ON CLIENT',
        ]
    },
]

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/)
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
}

const UserCard: React.FC<{
    user: OpenOrderUser
    animate: boolean
    expanded: boolean
    onToggle: () => void
}> = ({ user, animate, expanded, onToggle }) => {
    const [photoFailed, setPhotoFailed] = useState(false)
    const statusMap = new Map(user.statuses.map(s => [s.status, s]))
    const ecdEntry = statusMap.get('ECD TODAY/LATE')
    const closingEntry = statusMap.get('CLOSING TODAY/LATE')
    const ecdCount = ecdEntry?.count ?? 0
    const closingCount = closingEntry?.count ?? 0
    const initials = getInitials(user.name)
    const fadeClass = animate ? 'animate-fade-in' : ''
    const assignedStatusCount = statusMap.get('ASSIGNED')?.count ?? 0
    const auditHref = `?pg=orders&status=OPEN&assigned=ALL&custom=auditor|${user.userId}`
    const auditPillLink = user.adminLevel >= 60 && user.userId !== 'UNASSIGNED'
    const AuditingPill = auditPillLink ? 'a' : 'span'

    useEffect(() => { setPhotoFailed(false) }, [user.photoUrl])

    return (
        <div className={`glass-card --light oo-card${fadeClass ? ' ' + fadeClass : ''}`}>
            {/* Top color bar — dynamic, stays inline */}
            <div style={{ height: 4, background: user.color, flexShrink: 0 }} />

            {/* Header */}
            <div className="oo-card-header" onClick={onToggle}>
                <div className="oo-card-header-inner">
                    <div className="oo-avatar-wrap">
                        <div className="oo-avatar" style={{ background: user.color }}>
                            {user.photoUrl && !photoFailed ? (
                                <img
                                    src={user.photoUrl}
                                    alt={`${user.name} profile`}
                                    style={AVATAR_PHOTO_STYLE}
                                    onError={() => setPhotoFailed(true)}
                                />
                            ) : initials}
                        </div>
                        <div className="oo-avatar-dot" />
                    </div>

                    <div className="oo-card-info">
                        <div className="oo-name">{user.name}</div>
                        <div className="oo-card-meta">
                            <span className="oo-count-value">{user.openCount}</span>
                            <span className="oo-count-label">Open Orders</span>
                        </div>
                    </div>
                </div>

                <div className="oo-pills">

                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <span className="oo-pill">
                                <span className="oo-pill-dot oo-pill-dot--red" />
                                {user.assignedToday} Assigned
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>Assigned Today</TooltipContent>
                    </Tooltip>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <span className="oo-pill">
                                <span className="oo-pill-dot oo-pill-dot--green" />
                                {user.invoicedToday} Invoiced
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>Invoiced Today</TooltipContent>
                    </Tooltip>

                    {
                        user.adminLevel >= 60 && user.userId !== 'UNASSIGNED'
                            ? (
                                <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                        <AuditingPill
                                            className="oo-pill"
                                            {...(auditPillLink
                                                ? { href: auditHref, onClick: (e: React.MouseEvent) => e.stopPropagation() }
                                                : {})}
                                        >
                                            <span className="oo-pill-dot oo-pill-dot--blue" />
                                            <span style={AUDITING_PILL_LABEL_STYLE}>
                                                {user.isAuditingCount} Auditing
                                            </span>
                                        </AuditingPill>
                                    </TooltipTrigger>
                                    <TooltipContent>Orders the user is auditing</TooltipContent>
                                </Tooltip>
                            )
                            : null
                    }
                </div>
            </div>

            {/* Expanded status sections */}
            {expanded && (
                <div className="oo-expanded">
                    {STATUS_SECTIONS.map(section => {
                        const rows = section.statuses
                            .map(s => statusMap.get(s))
                            .filter((e): e is OrderStatusEntry => e !== undefined)
                        if (!rows.length) return null
                        return (
                            <div key={section.label} className="oo-section">
                                <div className="oo-section-header">
                                    <div className="oo-section-accent" />
                                    <span className="oo-section-label">{section.label}</span>
                                </div>
                                {rows.map((row, i) => (
                                    <div key={i} className="oo-section-row" style={{ opacity: row.count === 0 ? 0.4 : 1 }}>
                                        {row.count > 0 ? (
                                            <a href={row.href} className="oo-section-link">{row.status}</a>
                                        ) : (
                                            <span className="oo-section-text">{row.status}</span>
                                        )}
                                        <span className="oo-section-count">{row.count}</span>
                                    </div>
                                ))}
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Footer buttons */}
            <div className="oo-footer">
                {ecdEntry?.href
                    ? <a href={ecdEntry.href} className="oo-btn-ecd">
                        <span className="oo-btn-ecd-value">{ecdCount}</span>
                        <span className="oo-btn-ecd-label">ECD Today/Late</span>
                    </a>
                    : <div className="oo-btn-ecd">
                        <span className="oo-btn-ecd-value">{ecdCount}</span>
                        <span className="oo-btn-ecd-label">ECD Today/Late</span>
                    </div>
                }
                {closingEntry?.href
                    ? <a href={closingEntry.href} className="oo-btn-closing">
                        <span className="oo-btn-closing-value">{closingCount}</span>
                        <span className="oo-btn-closing-label">Closing Today/Late</span>
                    </a>
                    : <div className="oo-btn-closing">
                        <span className="oo-btn-closing-value">{closingCount}</span>
                        <span className="oo-btn-closing-label">Closing Today/Late</span>
                    </div>
                }
            </div>
        </div>
    )
}

function ssGet(key: string) { try { return sessionStorage.getItem(key) } catch { return null } }
function ssSet(key: string, val: string) { try { sessionStorage.setItem(key, val) } catch { } }

export const OpenOrders: React.FC<OpenOrdersProps> = ({ users, animate = false }) => {
    const [expandedSet, setExpandedSet] = useState<Set<string>>(() => {
        const raw = ssGet('oo_expanded')
        return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>()
    })
    const [showAll, setShowAll] = useState(() => ssGet('oo_show_all') === '1')
    const [groupByColor, setGroupByColor] = useState(() => ssGet('oo_group') === '1')

    console.log('users', users)

    useEffect(() => { ssSet('oo_expanded', JSON.stringify(Array.from(expandedSet))) }, [expandedSet])
    useEffect(() => { ssSet('oo_show_all', showAll ? '1' : '0') }, [showAll])
    useEffect(() => { ssSet('oo_group', groupByColor ? '1' : '0') }, [groupByColor])

    if (!users?.length) return null

    const handleShowAll = (checked: boolean) => {
        setShowAll(checked)
        setExpandedSet(checked ? new Set(users.map(u => u.userId)) : new Set())
    }

    const handleToggle = (userId: string) => {
        setExpandedSet(prev => {
            const next = new Set(prev)
            if (next.has(userId)) next.delete(userId)
            else next.add(userId)
            return next
        })
        setShowAll(false)
    }

    const colorGroups: { color: string; users: OpenOrderUser[] }[] = []
    const colorIndex = new Map<string, number>()
    for (const user of users) {
        const key = user.color
        if (!colorIndex.has(key)) {
            colorIndex.set(key, colorGroups.length)
            colorGroups.push({ color: key, users: [] })
        }
        colorGroups[colorIndex.get(key)!].users.push(user)
    }

    const renderCard = (user: OpenOrderUser) => (
        <UserCard
            key={user.userId}
            user={user}
            animate={animate}
            expanded={expandedSet.has(user.userId)}
            onToggle={() => handleToggle(user.userId)}
        />
    )

    return (
        <TooltipProvider>
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <label htmlFor="open-orders-show-all" className="oo-label">
                    <input type="checkbox" id="open-orders-show-all" className="oo-checkbox" checked={showAll} onChange={e => handleShowAll(e.target.checked)} />
                    Show all order details
                </label>
                <label htmlFor="open-orders-group" className="oo-label">
                    <input type="checkbox" id="open-orders-group" className="oo-checkbox" checked={groupByColor} onChange={e => setGroupByColor(e.target.checked)} />
                    Group by
                </label>
            </div>

            {groupByColor ? (
                <div className="oo-groups">
                    {colorGroups.map(group => (
                        <div key={group.color} className="oo-group">
                            <div className="oo-grid-group">{group.users.map(renderCard)}</div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="oo-grid">{users.map(renderCard)}</div>
            )}
        </TooltipProvider>
    )
}

export default OpenOrders
